import { Capacitor } from '@capacitor/core';
import { Box, Button, Container, Dialog, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isAndroid, isIOS } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useFormStyles } from '../pages/Auth/authStyles';
import loginLogo from '../assets/svg/new-logo-with-title-white.svg';
import { Colors } from '../assets/theme/Colors';
import { hideNativePrompt } from '../actions/ui';

const iOSAppStoreURL = 'https://apps.apple.com/app/id6761080192';
const androidGooglePlayURL = 'https://play.google.com/store/apps/details?id=us.foodrescue.foodrescueus';

const NativeAppPrompt = ({ show }) => {
  const authStyles = useFormStyles();
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'));
  const canShowPrompt = isMobileView && !Capacitor.isNativePlatform();
  const hidePrompt = useSelector((state) => state.ui.hideNativePrompt);
  const [open, setOpen] = useState(canShowPrompt && show && !hidePrompt);
  const isInitialRender = useRef(true);
  const offset = `calc(${theme.mixins.toolbar.minHeight + 8}px + var(--ion-safe-area-top))`;

  const onDialogClose = () => {
    setOpen(false);
    dispatch(hideNativePrompt());
  };

  useEffect(() => {
    // Do not close prompt on initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    onDialogClose();
  }, [location.pathname]);

  if (!canShowPrompt || hidePrompt) {
    return null;
  }

  return (
    <Box>
      <Dialog
        disablePortal
        open={open}
        sx={{
          position: 'absolute',
          zIndex: 1200,
          '.MuiPaper-root': { mx: 1, marginTop: offset },
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
        }}
      >
        <Box sx={authStyles.pageBackground}>
          <Container maxWidth="md">
            <Grid container spacing={2} sx={authStyles.pageContainer} data-testid="login-container">
              <Grid
                item
                xs
                sx={{
                  padding: '16px 0 !important',
                  width: '100%',
                }}
              >
                <Grid item xs sx={[authStyles.logoWrapper, { py: 3 }]}>
                  <Box component="img" src={loginLogo} alt="logo" sx={authStyles.logo} />
                </Grid>

                <Grid item xs sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      maxWidth: 'fit-content',
                      padding: { xs: '16px', sm: '16px 32px' },
                    }}
                  >
                    <Typography
                      sx={{ color: Colors.white, fontWeight: 500, textAlign: 'center', pb: 2 }}
                      variant="body1"
                    >
                      Looks like you&apos;re on mobile!
                    </Typography>
                    <Typography
                      sx={{ color: Colors.white, fontWeight: 500, textAlign: 'center', pb: 2 }}
                      variant="body1"
                    >
                      For the best performance, and access to our newest features, we recommend using the mobile app.
                    </Typography>
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                      <Button
                        component="a"
                        href={isIOS ? iOSAppStoreURL : androidGooglePlayURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{ textTransform: 'none' }}
                      >
                        Use the App
                      </Button>
                    </Box>
                    <Box sx={{ py: 1, textAlign: 'center' }}>
                      <Button
                        variant="text"
                        onClick={onDialogClose}
                        sx={{
                          color: Colors.white,
                          textDecoration: 'underline',
                          textTransform: 'none',
                          padding: 0,
                          fontSize: '16px',
                        }}
                      >
                        Continue in browser
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Dialog>
    </Box>
  );
};

export default NativeAppPrompt;
